/**
 * AdminAthleteMergePage — /admin/athletes/merge (Sprint 3 Lot A.3)
 *
 * Admin uniquement. Sélection de 2 profils → aperçu comparatif → choix
 * du profil principal → confirmation forte → exécution atomique.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { showError } from "@/lib/errors";
import { resolveAthletePhoto } from "@/lib/photoFallback";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, ArrowLeft, GitMerge, Search, AlertTriangle, Crown } from "lucide-react";

export default function AdminAthleteMergePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isAdmin = (user?.roles || []).includes("admin");

  const [sourceId, setSourceId] = useState(params.get("source") || "");
  const [targetId, setTargetId] = useState(params.get("target") || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [primaryId, setPrimaryId] = useState(""); // choix explicite Admin
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey] = useState(() => (globalThis.crypto?.randomUUID?.() || `merge-${Date.now()}-${Math.random()}`));

  const canPreview = sourceId && targetId && sourceId !== targetId;

  const loadPreview = useCallback(async () => {
    if (!canPreview) return;
    setLoading(true);
    setPreview(null);
    setPrimaryId("");
    try {
      const { data } = await api.get(`/admin/athletes/merge/preview?source_id=${encodeURIComponent(sourceId)}&target_id=${encodeURIComponent(targetId)}`);
      setPreview(data);
      if (data.suggested_primary_id) setPrimaryId(data.suggested_primary_id);
    } catch (e) { showError(e, { prefix: "Aperçu :" }); }
    finally { setLoading(false); }
  }, [sourceId, targetId, canPreview]);

  useEffect(() => { if (canPreview) loadPreview(); }, [loadPreview, canPreview]);

  const doMerge = async () => {
    if (!primaryId) { toast.error("Choisissez explicitement le profil principal."); return; }
    const absorbedId = primaryId === sourceId ? targetId : sourceId;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/athletes/merge", {
        primary_id: primaryId,
        absorbed_id: absorbedId,
        reason: reason || null,
        idempotency_key: idempotencyKey,
        confirm: "FUSIONNER",
      });
      toast.success("Fusion réalisée avec succès.");
      setConfirmOpen(false);
      // Retour vers le profil principal
      nav(`/athletes/${primaryId}`);
    } catch (e) { showError(e, { prefix: "Fusion :" }); }
    finally { setBusy(false); }
  };

  if (!user) {
    return <div className="p-8"><Card className="p-6 text-center">Connectez-vous pour accéder à la fusion.</Card></div>;
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="merge-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">La fusion est réservée aux administrateurs. Les COPF peuvent signaler un doublon pour fusion.</p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/admin/athletes/duplicates")}><ArrowLeft className="h-4 w-4 mr-1.5" />Retour Doublons</Button>
        </Card>
      </div>
    );
  }

  const a = preview?.a;
  const b = preview?.b;
  const primaryProfile = primaryId === a?.id ? a : (primaryId === b?.id ? b : null);
  const absorbedProfile = primaryProfile ? (primaryProfile === a ? b : a) : null;
  const impact = primaryProfile
    ? (absorbedProfile === a ? preview?.impact_if_a_absorbed : preview?.impact_if_b_absorbed)
    : null;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-merge-page">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <GitMerge className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />Fusion de profils athlètes
        </h1>
        <p className="text-sm text-muted-foreground">Admin uniquement. Réconcilie deux profils (résultats, EntityLinks, contenus, compétitions) et redirige les anciens liens.</p>
      </div>

      <Card className="p-3">
        <div className="grid sm:grid-cols-[1fr_auto_1fr_auto] items-end gap-2">
          <AthleteAutocomplete
            label="Profil A (source)"
            id="src"
            value={sourceId}
            onSelect={(a) => setSourceId(a.id)}
            testId="merge-input-source"
          />
          <div className="text-center text-2xl text-muted-foreground pb-2">↔</div>
          <AthleteAutocomplete
            label="Profil B (à conserver ou absorbé)"
            id="tgt"
            value={targetId}
            onSelect={(a) => setTargetId(a.id)}
            testId="merge-input-target"
          />
          <Button onClick={loadPreview} disabled={!canPreview || loading} data-testid="merge-load-preview"><Search className="h-4 w-4 mr-1.5" />Aperçu</Button>
        </div>
      </Card>

      {loading && <div className="grid sm:grid-cols-2 gap-3"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>}

      {preview && (
        <>
          {(preview.warnings || []).length > 0 && (
            <Card className="p-3 border-amber-300 bg-amber-50" data-testid="merge-warnings">
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <ul className="space-y-1">
                  {(preview.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-3" data-testid="merge-side-by-side">
            <ProfileCard label="Profil A" athlete={a} />
            <ProfileCard label="Profil B" athlete={b} />
          </div>

          <Card className="p-4 space-y-3" data-testid="merge-primary-choice">
            <div className="text-sm font-semibold flex items-center gap-2"><Crown className="h-4 w-4 text-[hsl(var(--aito-gold))]" />Choix du profil principal</div>
            <p className="text-xs text-muted-foreground">Le profil principal conservera son URL / slug. L'autre sera marqué « fusionné » et redirigé vers le principal.</p>
            {preview.suggested_primary_id && (
              <p className="text-xs text-[hsl(var(--aito-lagoon))]">Suggestion basée sur l'ownership revendiqué.</p>
            )}
            <RadioGroup value={primaryId} onValueChange={setPrimaryId}>
              <div className="grid sm:grid-cols-2 gap-2">
                {[a, b].map((x) => x && (
                  <label key={x.id} className={`flex items-start gap-2 border rounded-md p-2 cursor-pointer hover:bg-secondary/40 ${primaryId === x.id ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/5" : ""}`}>
                    <RadioGroupItem value={x.id} data-testid={`merge-primary-${x.id === a.id ? "a" : "b"}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{x.full_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono truncate">{x.id}</div>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>

            {primaryProfile && impact && (
              <div className="mt-2 rounded-md border-l-4 border-[hsl(var(--aito-lagoon))] bg-secondary/40 p-3 text-xs" data-testid="merge-impact-summary">
                <div className="font-semibold mb-1">Aperçu des conséquences (avant validation) :</div>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
                  <li>• Profil principal conservé : <strong>{primaryProfile.full_name}</strong></li>
                  <li>• Profil absorbé : <strong>{absorbedProfile?.full_name}</strong> → marqué <em>fusionné</em></li>
                  <li>• EntityLinks à réconcilier : <strong>{impact.entity_links}</strong></li>
                  <li>• Curator associations : <strong>{impact.curator_associations}</strong></li>
                  <li>• Articles associations : <strong>{impact.user_article_associations}</strong></li>
                  <li>• Compétitions liées : <strong>{impact.competitions_linked}</strong></li>
                  <li>• Calendrier (participations) : <strong>{impact.calendar_participants}</strong></li>
                  <li>• Mémoire : <strong>{impact.memory_items}</strong> · News : <strong>{impact.news_items}</strong> · Lives : <strong>{impact.lives}</strong></li>
                  <li>• Hero pins reassignés : <strong>{impact.hero_pins}</strong></li>
                  <li>• Claims transférés : <strong>{impact.claims}</strong></li>
                </ul>
                <div className="mt-2 text-muted-foreground">Une redirection <code className="text-[10px] bg-muted px-1 rounded">/athletes/{absorbedProfile?.slug}</code> → <code className="text-[10px] bg-muted px-1 rounded">/athletes/{primaryProfile?.slug}</code> sera créée automatiquement.</div>
              </div>
            )}

            <div>
              <Label htmlFor="reason">Motif (audit)</Label>
              <Textarea id="reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : doublon issu de l'import COPF_ROAD2027 le 12/09, athlète le confirme." data-testid="merge-reason" />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setConfirmOpen(true)} disabled={!primaryId} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="merge-open-confirm">
                <GitMerge className="h-4 w-4 mr-1.5" />Prévisualiser puis fusionner
              </Button>
            </div>
          </Card>
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Confirmer la fusion</DialogTitle>
            <DialogDescription>
              L'opération est {" "}<strong>quasi-irréversible</strong>. Toutes les relations, résultats et contenus du profil absorbé seront transférés vers le profil principal. Une redirection de slug sera créée.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1 py-1">
            <div>Principal : <strong>{primaryProfile?.full_name}</strong> (<code className="text-[10px] bg-muted px-1 rounded">{primaryProfile?.id}</code>)</div>
            <div>Absorbé : <strong>{absorbedProfile?.full_name}</strong> (<code className="text-[10px] bg-muted px-1 rounded">{absorbedProfile?.id}</code>)</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={doMerge} disabled={busy} className="bg-destructive text-white hover:bg-destructive/90" data-testid="merge-confirm-submit">
              {busy ? "Fusion en cours…" : "Exécuter la fusion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileCard({ label, athlete }) {
  if (!athlete) return null;
  return (
    <Card className="p-4" data-testid={`merge-profile-${label.toLowerCase().replace(" ", "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        {athlete.claim_status === "claimed" && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">revendiqué</Badge>}
      </div>
      <div className="text-lg font-display font-semibold leading-tight">{athlete.full_name}</div>
      <div className="text-[10px] text-muted-foreground font-mono break-all">{athlete.id}</div>
      <div className="mt-2 text-xs space-y-0.5">
        <div>Discipline : <strong>{athlete.main_discipline || "—"}</strong></div>
        <div>Année : <strong>{athlete.period_start || "—"}</strong></div>
        <div>Owner : <strong>{athlete.owner_user_id || "—"}</strong></div>
        <div>Source de création : <strong>{athlete.creation_source || "—"}</strong></div>
        <div>Import batch : <strong>{athlete.import_source_key || "—"}</strong></div>
        <div>Slug : <code className="text-[10px] bg-muted px-1 rounded">{athlete.slug}</code></div>
        <div>Résultats : <strong>{(athlete.results || []).length}</strong> · Palmarès : <strong>{(athlete.achievements || []).length}</strong> · Photos : <strong>{(athlete.photos || []).length}</strong></div>
        <div>Clubs : <strong>{(athlete.clubs || []).join(", ") || "—"}</strong></div>
        <div>Fédérations : <strong>{(athlete.federations || []).join(", ") || "—"}</strong></div>
        <div>Objectif JP 2027 : <strong>{athlete.road_to_2027 ? "oui" : "non"}</strong></div>
        <div>Créé : {athlete.created_at?.slice(0, 10)} · MàJ : {athlete.updated_at?.slice(0, 10)}</div>
      </div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// V11 Hotfix — Autocomplete athlètes pour la fusion (fin des UUIDs à copier-coller)
// ---------------------------------------------------------------------------
function AthleteAutocomplete({ label, id, value, onSelect, testId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!value) { setCurrent(null); return; }
    if (current?.id === value) return;
    api.get(`/athletes/${value}`).then(({ data }) => setCurrent(data)).catch(() => setCurrent(null));
  }, [value, current?.id]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/athletes", { params: { q, limit: 8 } });
        setResults(data.items || []);
      } finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  return (
    <div className="relative">
      <Label htmlFor={id}>{label}</Label>
      {current ? (
        <div className="mt-1 flex items-center justify-between border rounded-md px-2 py-1.5 bg-secondary/40" data-testid={`${testId}-selected`}>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{current.full_name}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {[current.main_discipline, (current.clubs || [])[0], current.owner_user_id ? "revendiqué" : "non revendiqué"].filter(Boolean).join(" · ")}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setCurrent(null); onSelect({ id: "" }); }} data-testid={`${testId}-clear`}>Changer</Button>
        </div>
      ) : (
        <>
          <Input
            id={id}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Rechercher un nom, prénom, email…"
            data-testid={testId}
            autoComplete="off"
          />
          {open && q.length >= 2 && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto border rounded-md bg-background shadow-lg" data-testid={`${testId}-dropdown`}>
              {busy && <div className="p-2 text-xs text-muted-foreground">Recherche…</div>}
              {!busy && results.length === 0 && <div className="p-2 text-xs text-muted-foreground">Aucun profil trouvé</div>}
              {results.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { onSelect(a); setCurrent(a); setOpen(false); setQ(""); }}
                  className="w-full text-left px-2 py-1.5 hover:bg-secondary/40 flex items-center gap-2"
                  data-testid={`${testId}-option-${a.id}`}
                >
                  <img src={resolveAthletePhoto(a)} alt="" className={`h-8 w-8 rounded-full ${a.photo_url ? "object-cover" : "object-cover bg-white"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.full_name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {[a.main_discipline, (a.clubs || [])[0], a.island].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
